import { useEffect, useMemo, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Typography } from '@screentone/core';
import isEqual from 'lodash/isEqual';

import { Article } from 'components/datamodel/content/Article/Article';
import PageDraftTrash from 'components/page-draft-trash/PageDraftTrash';
import { ShowFilterOptions } from 'components/search-content/components/search-content-filters/SearchContentFiltersContainer';
import { SearchContent, SearchParams } from 'components/search-content/SearchContent';
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DRAGGABLE_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { useDragAndDrop } from 'contexts/drag-and-drop/useDragAndDrop';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { ArticleItem } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent, AllessehQuerySortKeyTypes, AllessehQuerySortOrderType } from 'hooks/useAllessehContentQuery';
import { DEFAULT_CONTENT_SEARCH_FILTERS } from 'utils/constants';
import { determineDateDisplay, formatInTimeZone } from 'utils/dates';
import { NEWSLETTER_FILTERS } from 'utils/newsletters';
import { useScratchpadContext } from '../../scratch-pad/scratchpad-context';

export const PageContentSearch = ({
  resultListClassName,
  embargo,
  showFilters,
  newsletterName
}: {
  resultListClassName?: string;
  embargo?: boolean;
  showFilters?: ShowFilterOptions;
  newsletterName?: string;
}) => {
  const { renderActions: renderMenuActions } = useContextMenuActions();
  const currentProperty = useConvertedProperty();
  const { getAllArticleIds, fromAllessehContent } = useDataModelContext();
  const { generateDraggableId } = useDragAndDrop();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const { pageTypeSetting, structureTreatmentTypes = [] } = usePageTypeSettingsContext();

  const excludedArticleIds = getAllArticleIds();
  const scratchpadCtx = useScratchpadContext();

  // using useMemo here to prevent linting errors
  let additionalFilters = useMemo(() => ({}), []);

  if (newsletterName) {
    additionalFilters = NEWSLETTER_FILTERS[currentProperty as string][newsletterName];
  }

  const defaultFilterState = useMemo(() => {
    if (!publicationSettingsResp || (!pageTypeSetting && !structureTreatmentTypes.length)) {
      return null;
    }

    const { searchableContentTypes, defaultContentTypes } = publicationSettingsResp.publicationSetting;
    const publishStatusValues =
      pageTypeSetting && pageTypeSetting.contentTypes.length > 0
        ? pageTypeSetting.contentTypes.filter((ct) => searchableContentTypes.includes(ct))
        : searchableContentTypes;

    let contentTypes = defaultContentTypes.filter((option) => publishStatusValues.includes(option));

    const { embargoSearchableContentTypes } = publicationSettingsResp.publicationSetting;

    // If embargo is enabled, we need to include the embargo searchable content types in the search filters, we append, not overwrite.
    if (embargo && Array.isArray(embargoSearchableContentTypes) && embargoSearchableContentTypes.length > 0) {
      // Use a Set to ensure there are no duplicate content types.
      contentTypes = [...new Set([...contentTypes, ...embargoSearchableContentTypes])];
    }

    return {
      ...DEFAULT_CONTENT_SEARCH_FILTERS,
      contentTypes,
      excludedContentIds: scratchpadCtx
        ? [...excludedArticleIds, ...scratchpadCtx.getAllScratchpadItemIds()]
        : excludedArticleIds,
      embargo,
      ...additionalFilters
    };
  }, [publicationSettingsResp, pageTypeSetting, structureTreatmentTypes, scratchpadCtx, excludedArticleIds, embargo, additionalFilters]);

  const [searchParams, setSearchParams] = useState<SearchParams | null>(null);

  useEffect(() => {
    if (defaultFilterState && !searchParams) {
      setSearchParams({
        search: '',
        filters: defaultFilterState,
        sort: embargo ? AllessehQuerySortKeyTypes.EmbargoDate : AllessehQuerySortKeyTypes.LiveDate,
        sortOrder: embargo ? AllessehQuerySortOrderType.ASC : AllessehQuerySortOrderType.DESC
      });
    }
  }, [defaultFilterState, embargo, searchParams]);

  useEffect(() => {
    if (!publicationSettingsResp) {
      return;
    }

    setSearchParams((prev) => {
      if (!prev) {
        return prev;
      }

      const prevExcluded = prev.filters.excludedContentIds ?? [];

      if (
        isEqual(
          prevExcluded,
          scratchpadCtx ? [...excludedArticleIds, ...scratchpadCtx.getAllScratchpadItemIds()] : excludedArticleIds
        )
      ) {
        return prev;
      }

      return {
        ...prev,
        filters: {
          ...prev.filters,
          excludedContentIds: scratchpadCtx
            ? [...excludedArticleIds, ...scratchpadCtx.getAllScratchpadItemIds()]
            : excludedArticleIds
        }
      };
    });
  }, [excludedArticleIds, publicationSettingsResp, scratchpadCtx]);

  const handleSearchParamsChange = (changedParams: SearchParams) => {
    setSearchParams(changedParams);
  };

  let embargoDate: string;
  let embargoTime: string;
  let formattedDate: string;
  let formattedTime: string;
  let showDate: boolean;
  let showTime: boolean;

  return (
    defaultFilterState &&
    searchParams && (
      <div>
        <SearchContent
          beforeContent={<PageDraftTrash scratchPadHeight={embargo ? 0 : scratchpadCtx?.height} />}
          embargo={embargo}
          searchProps={{
            defaultFilterState,
            onSearchParamsChange: handleSearchParamsChange,
            resultListClassName,
            searchParams,
            showFilters,
            renderContentCard: (content: AllessehContent, index: number) => (
              <Draggable draggableId={generateDraggableId(DRAGGABLE_PREFIXES.MODULE_ITEM, { content })} index={index}>
                {(draggableProvided) => {
                  const articleItem = fromAllessehContent(content) as ArticleItem;
                  if (embargo) {
                    const dateObject = new Date(content.data.attributes.embargo_datetime_utc as string);
                    formattedDate = formatInTimeZone(
                      dateObject,
                      publicationSettingsResp?.publicationSetting.preferredTimezone,
                      'LLL d, yyyy'
                    );
                    formattedTime = formatInTimeZone(
                      dateObject,
                      publicationSettingsResp?.publicationSetting.preferredTimezone,
                      'h:mm aaaa',
                      { showTimezoneAbbreviation: true }
                    );
                    ({ showTime, showDate, embargoTime, embargoDate } = determineDateDisplay(
                      embargoDate,
                      formattedDate,
                      embargoTime,
                      formattedTime
                    ));
                  }
                  return (
                    <>
                      {embargo && (index < 1 || showDate) && (
                        <Typography margin={{ top: 'sm', bottom: 'md' }} data-testid="page-content-search-date-label">
                          {formattedDate}
                        </Typography>
                      )}
                      {embargo && (index < 1 || showTime) && (
                        <Typography
                          variant="label3"
                          margin={{ bottom: 'sm' }}
                          data-testid="page-content-search-time-label"
                        >
                          {formattedTime}
                        </Typography>
                      )}
                      <div
                        data-testid="page-content-search-draggable-items"
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        <Article
                          data={articleItem}
                          renderActions={() =>
                            renderMenuActions(MenuActionsKeys.ArticleSearch, { entity: articleItem })
                          }
                        />
                      </div>
                    </>
                  );
                }}
              </Draggable>
            )
          }}
        />
      </div>
    )
  );
};
